import cn from 'classnames';
import 'keen-slider/keen-slider.min.css';
import { useKeenSlider } from 'keen-slider/react';
import Image from 'next/image';
import Link from 'next/link';
import { useState } from 'react';
import { AnchorArrow } from 'react-scale/components';
import { useMedia } from 'react-use';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';

const Slider = ({ slides = [] }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const isDesktop = useMedia('(min-width: 1024px)');
  const [sliderRef, slider] = useKeenSlider({
    loop: false,
    drag: !isDesktop,
    slides: {
      origin: 'center',
      perView: 1,
      spacing: 16
    },
    slideChanged(s) {
      setCurrentSlide(s.track.details.rel);
    },
    created() {
      setLoaded(true);
    }
  });

  return (
    <>
      <div ref={sliderRef} className="relative keen-slider">
        {loaded && slider.current && (
          <>
            <button
              onClick={() => slider.current?.prev()}
              className="absolute z-10 hidden w-10 h-10 transition-colors duration-200 -translate-y-1/2 rounded-full bg-neutral-950 not:disabled:hover:bg-black lg:block disabled:cursor-not-allowed disabled:opacity-60 focus:outline-white not:disabled:focus:outline-1 focus:outline-offset-1 left-4 top-1/2"
              type="button"
              disabled={currentSlide === 0}
            >
              {'<-'}
            </button>
            <button
              onClick={() => slider.current?.next()}
              className="absolute z-10 hidden w-10 h-10 transition-colors duration-200 -translate-y-1/2 rounded-full bg-neutral-950 not:disabled:hover:bg-black lg:block disabled:cursor-not-allowed disabled:opacity-60 focus:outline-white not:disabled:focus:outline-1 focus:outline-offset-1 right-4 top-1/2"
              type="button"
              disabled={currentSlide === slides.length - 1}
            >
              {'->'}
            </button>
          </>
        )}
        {slides.map((slide, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <div key={index} className="flex keen-slider__slide">
            <Image
              unoptimized
              src={slide.src}
              alt={slide.alt}
              width={slide.width}
              height={slide.height}
              layout="intrinsic"
              quality={100}
            />
          </div>
        ))}
      </div>
      {loaded && slider && (
        <div className="flex justify-center mx-auto mt-6 space-x-2 max-w-max lg:hidden">
          {[...Array(slider.current.slides.length).keys()].map(idx => (
            <span
              key={idx}
              onClick={() => slider.current?.moveToIdx(idx)}
              className={cn(
                'cursor-pointer rounded-full w-2 h-2',
                currentSlide === idx ? 'bg-white' : 'bg-plain-neutral-4'
              )}
            />
          ))}
        </div>
      )}
    </>
  );
};

const Feature = ({ content }) =>
  content.link ? (
    <li className="list-none">
      <Link
        href={content.link.href || '#'}
        key={content.title}
        target={content.link?.openInNewTab && '_blank'}
      >
        <Content content={content} />
      </Link>
    </li>
  ) : (
    <li className="overflow-hidden list-none">
      <Content content={content} />
    </li>
  );

const Content = ({ content }) => (
  <>
    {content.img && (
      <figure
        className={cn('relative mb-6 w-full', {
          'overflow-hidden rounded-2xl mb-10': content.img.borderRadius,
          'max-h-[300px] h-[33vh] md:[200px] lg:h-[15vw] lg:max-h-[230px]': content.img.cover
        })}
      >
        {content.img.cover ? (
          <Image
            src={content.img.url}
            alt={content.img.alt ?? ''}
            quality={100}
            className="object-cover"
            sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
            fill
          />
        ) : (
          <Image
            unoptimized
            src={content.img.url}
            alt={content.img.alt ?? ''}
            quality={100}
            height={content.img.height ?? 40}
            width={content.img.width ?? 40}
            sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
          />
        )}
      </figure>
    )}
    {content.slider && (
      <div className="mb-6">
        <Slider slides={content.slider.slides} />
      </div>
    )}
    {content.overtitle && (
      <h4 className="mb-4 text-overtitle-sm text-gradient">{content.overtitle}</h4>
    )}
    <h1 className="mb-5 text-2xl leading-10 text-white font-aeonik">{content.title}</h1>
    <ReactMarkdown
      className="text-neutral-400 prose-a:light:text-link prose-a:dark:text-white max-w-none"
      rehypePlugins={[rehypeRaw]}
    >
      {content.text}
    </ReactMarkdown>
    {content.list && (
      <ul className="mt-6">
        {content.list.map(item => (
          <li className="mb-2" key={item}>
            <p className="text-base leading-relaxed text-neutral-400">• {item}</p>
          </li>
        ))}
      </ul>
    )}
    {content.checklist && (
      <ul className="pt-8 mt-4 space-y-4 border-t border-neutral-900">
        {content.checklist.map(each => {
          // from Builder comes as each.item, but locally we use it as a string
          const item = typeof each === 'string' ? each : each.item;
          return (
            <li key={item}>
              <span className="flex space-x-4">
                <CheckCircle />
                <p className="flex-1 text-base leading-relaxed text-white">{item}</p>
              </span>
            </li>
          );
        })}
      </ul>
    )}
    {content.link && content.link?.text && (
      <p className="mt-6 max-w-max border-b">
        <AnchorArrow
          text={content.link.text}
          href={content.link.href || '#'}
          openInNewTab={content.link?.openInNewTab}
        />
      </p>
    )}
  </>
);

const CheckCircle = () => (
  <svg width="24" height="25" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12.3013" r="12" fill="#D1AAD7" />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M17.3789 6.8376C17.7601 6.52701 18.3248 6.49878 18.7201 6.82348C19.1718 7.20466 19.2283 7.86819 18.833 8.31995L11.3789 17.2705C11.2518 17.4258 11.1106 17.567 10.9412 17.68C9.96712 18.3294 8.65418 18.0611 8.00477 17.087L5.0683 12.6823C4.74359 12.2023 4.88477 11.5388 5.36477 11.2141C5.73183 10.9741 6.19771 10.9882 6.53653 11.2141L9.6283 13.2611L17.3648 6.82348L17.3789 6.8376Z"
      fill="black"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M12 24.3013C18.6274 24.3013 24 18.9287 24 12.3013C24 5.67385 18.6274 0.30127 12 0.30127C5.37258 0.30127 0 5.67385 0 12.3013C0 18.9287 5.37258 24.3013 12 24.3013ZM12 24.3013C5.37882 24.3013 0 18.9224 0 12.3013C0 5.68009 5.37882 0.30127 12 0.30127C18.6212 0.30127 24 5.68009 24 12.3013C24 18.9224 18.6212 24.3013 12 24.3013Z"
      fill="#E5E5E5"
    />
  </svg>
);

export default Feature;
